/**
 * Photo Cache Service - IndexedDB based offline photo caching
 * Stores photos locally when offline and syncs them when connection is restored
 */

const DB_NAME = 'TRBPhotoCache';
const DB_VERSION = 1;
const STORE_NAME = 'pendingPhotos';

let db = null;

// Initialize IndexedDB
export const initPhotoCache = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('PhotoCache IndexedDB initialized');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      // Create object store for pending photos
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('reportId', 'reportId', { unique: false });
        store.createIndex('criterionId', 'criterionId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

// Save photo to cache for later upload
export const cachePhotoForUpload = async (reportId, criterionId, photoData, fileName, contentType) => {
  await initPhotoCache();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const photoEntry = {
      reportId,
      criterionId,
      photoData, // base64 encoded
      fileName,
      contentType,
      timestamp: Date.now(),
      status: 'pending'
    };
    
    const request = store.add(photoEntry);
    
    request.onsuccess = () => {
      console.log('Photo cached for upload:', request.result);
      resolve(request.result);
    };
    
    request.onerror = () => {
      console.error('Failed to cache photo:', request.error);
      reject(request.error);
    };
  });
};

// Get all pending photos for a report
export const getPendingPhotos = async (reportId = null) => {
  await initPhotoCache();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    let request;
    if (reportId) {
      const index = store.index('reportId');
      request = index.getAll(reportId);
    } else {
      request = store.getAll();
    }
    
    request.onsuccess = () => {
      resolve(request.result || []);
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

// Get count of pending photos
export const getPendingPhotoCount = async () => {
  await initPhotoCache();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.count();
    
    request.onsuccess = () => {
      resolve(request.result);
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

// Remove photo from cache after successful upload
export const removePhotoFromCache = async (photoId) => {
  await initPhotoCache();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(photoId);
    
    request.onsuccess = () => {
      console.log('Photo removed from cache:', photoId);
      resolve();
    };
    
    request.onerror = () => {
      console.error('Failed to remove photo from cache:', request.error);
      reject(request.error);
    };
  });
};

// Clear all cached photos for a report
export const clearReportPhotoCache = async (reportId) => {
  const photos = await getPendingPhotos(reportId);
  
  for (const photo of photos) {
    await removePhotoFromCache(photo.id);
  }
};

// Sync pending photos to server
export const syncPendingPhotos = async (uploadFunction) => {
  const pendingPhotos = await getPendingPhotos();
  
  if (pendingPhotos.length === 0) {
    return { synced: 0, failed: 0 };
  }
  
  console.log(`Syncing ${pendingPhotos.length} pending photos...`);
  
  let synced = 0;
  let failed = 0;
  
  for (const photo of pendingPhotos) {
    try {
      // Convert base64 back to file for upload
      const response = await fetch(`data:${photo.contentType};base64,${photo.photoData}`);
      const blob = await response.blob();
      const file = new File([blob], photo.fileName, { type: photo.contentType });
      
      // Call the upload function
      await uploadFunction(photo.reportId, photo.criterionId, [file]);
      
      // Remove from cache on success
      await removePhotoFromCache(photo.id);
      synced++;
    } catch (error) {
      console.error('Failed to sync photo:', photo.id, error);
      failed++;
    }
  }
  
  return { synced, failed };
};

// Check if we're online - more reliable check
export const isOnline = () => {
  // navigator.onLine can give false positives
  // but false negatives are rare, so we trust it when it says offline
  return navigator.onLine;
};

// More reliable online check with actual network request
export const checkNetworkConnection = async () => {
  if (!navigator.onLine) return false;
  
  try {
    // Try to fetch a small resource to verify actual connectivity
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const response = await fetch('/api/health', {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    // If fetch fails, we might still be online but API is down
    // Return true to attempt the upload anyway
    return navigator.onLine;
  }
};

// Convert file to base64 for caching
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix to get just the base64 string
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Setup online/offline event listeners
export const setupNetworkListeners = (onOnline, onOffline) => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

export default {
  initPhotoCache,
  cachePhotoForUpload,
  getPendingPhotos,
  getPendingPhotoCount,
  removePhotoFromCache,
  clearReportPhotoCache,
  syncPendingPhotos,
  isOnline,
  fileToBase64,
  setupNetworkListeners
};
